import React, { useCallback, useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import useSellerPlanAccess from '../../hooks/useSellerPlanAccess';
import {
  claimSellerPlanTrial,
  confirmSellerPlanCheckout,
  fetchSellerPlanChoices,
  startSellerPlanCheckout
} from '../../services/membershipPlanService';

const { FiCheck, FiCreditCard, FiLoader, FiRefreshCw, FiShield } = FiIcons;

const SellerMembershipPlanTab = ({ seller }) => {
  const { access, loading, error: accessError, refresh } = useSellerPlanAccess(seller?.id);
  const [plans, setPlans] = useState([]);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const loadPlans = useCallback(async () => {
    if (!seller?.id) return;
    try {
      setPlans(await fetchSellerPlanChoices());
    } catch (loadError) {
      setError(loadError.message || 'Unable to load available plans.');
    }
  }, [seller?.id]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const checkoutStatus = query.get('plan_checkout');
    const sessionId = query.get('session_id');
    if (checkoutStatus !== 'success' || !sessionId || !seller?.id) return;

    let cancelled = false;
    setBusy('confirm');
    setError('');
    confirmSellerPlanCheckout(sessionId)
      .then(async () => {
        if (cancelled) return;
        setNotice('Stripe verified your recurring payment and activated the plan.');
        await refresh();
        query.delete('plan_checkout');
        query.delete('session_id');
        const nextQuery = query.toString();
        window.history.replaceState(
          {},
          document.title,
          `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash}`
        );
      })
      .catch((confirmError) => {
        if (!cancelled) setError(confirmError.message || 'Payment verification is still pending.');
      })
      .finally(() => {
        if (!cancelled) setBusy('');
      });

    return () => {
      cancelled = true;
    };
  }, [refresh, seller?.id]);

  const choosePlan = async (plan) => {
    setBusy(plan.id);
    setError('');
    setNotice('');
    try {
      if (plan.plan_type === 'trial') {
        await claimSellerPlanTrial(plan.id);
        setNotice(`${plan.name} trial is active for ${plan.trial_duration_days} days.`);
        await refresh();
        await loadPlans();
        return;
      }

      const checkout = await startSellerPlanCheckout(plan.id);
      if (!checkout?.url) throw new Error('Stripe did not return a checkout URL.');
      window.location.assign(checkout.url);
    } catch (actionError) {
      setError(actionError.message || 'Unable to change your seller plan.');
    } finally {
      setBusy('');
    }
  };

  const featureEntries = Object.entries(access?.features || {});
  const comparisonPlans = plans.filter((plan) => plan.is_active).slice(0, 3);
  const disabledPlans = plans.filter((plan) => !plan.is_active);
  const featureRows = [...new Set(
    comparisonPlans.flatMap((plan) => Object.entries(plan.features || {})
      .filter(([, detail]) => (
        Boolean(detail) && detail !== false && String(detail).toLowerCase() !== 'false'
      ))
      .map(([feature]) => feature))
  )].sort((first, second) => first.localeCompare(second));

  return (
    <section className="space-y-7">
      <header className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h2 className="text-3xl font-black tracking-tight text-slate-900">
            Seller <span className="text-indigo-600">Plans</span>
          </h2>
          <p className="mt-2 text-sm font-medium text-slate-500">
            Choose your seller tools. Paid access starts only after Stripe verifies recurring payment.
          </p>
        </div>
        <button
          type="button"
          onClick={() => { refresh(); loadPlans(); }}
          disabled={loading || !!busy}
          className="rounded-xl bg-slate-100 p-3 text-slate-600 disabled:opacity-50"
          aria-label="Refresh seller plan"
        >
          <SafeIcon icon={FiRefreshCw} />
        </button>
      </header>

      {(accessError || error) && (
        <p role="alert" className="rounded-xl bg-rose-50 p-4 text-sm font-bold text-rose-700">
          {error || accessError}
        </p>
      )}
      {notice && (
        <p role="status" className="rounded-xl bg-emerald-50 p-4 text-sm font-bold text-emerald-700">
          {notice}
        </p>
      )}

      <div className="rounded-[2rem] border border-indigo-100 bg-indigo-50/60 p-6">
        {loading || busy === 'confirm' ? (
          <SafeIcon icon={FiLoader} className="animate-spin text-2xl text-indigo-600" />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <div>
                <p className="text-xs font-black uppercase tracking-widest text-indigo-500">Current plan</p>
                <h3 className="mt-1 text-2xl font-black text-slate-900">{access?.plan_name || 'Free'}</h3>
                <p className="mt-1 text-sm font-bold capitalize text-slate-600">
                  {access?.billing_status || 'free'} · {access?.billing_source === 'stripe' ? 'Verified Stripe subscription' : access?.billing_source || 'free access'}
                </p>
              </div>
              <SafeIcon icon={FiShield} className="text-3xl text-indigo-500" />
            </div>
            {access?.trial_ends_at && access.billing_status === 'trialing' && (
              <p className="mt-3 text-xs font-bold text-indigo-700">
                Trial ends {new Date(access.trial_ends_at).toLocaleDateString()}.
              </p>
            )}
            {access?.current_period_end && (
              <p className="mt-3 text-xs font-bold text-indigo-700">
                Current billing period ends {new Date(access.current_period_end).toLocaleDateString()}.
              </p>
            )}
            {featureEntries.length > 0 ? (
              <ul className="mt-5 grid gap-2 sm:grid-cols-2">
                {featureEntries.map(([feature, description]) => (
                  <li key={feature} className="flex items-start gap-2 text-sm font-bold text-slate-700">
                    <SafeIcon icon={FiCheck} className="mt-0.5 shrink-0 text-emerald-600" />
                    <span>{feature}{description === true ? '' : ` — ${description}`}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="mt-5 text-sm font-medium text-slate-600">Your current plan has no additional feature entitlements.</p>
            )}
          </>
        )}
      </div>

      <div className="overflow-visible rounded-[2rem] border border-slate-200 bg-white shadow-sm">
        <div className="border-b border-slate-100 px-5 py-4 sm:px-6">
          <h3 className="text-lg font-black text-slate-900">Compare plans</h3>
          <p className="mt-1 text-sm font-medium text-slate-500">
            Compare every available seller feature before choosing a plan.
          </p>
        </div>
        <table className="w-full table-fixed border-separate border-spacing-0 text-left">
          <thead>
            <tr>
              <th className="sticky left-0 top-0 z-30 w-[24%] border-b border-slate-200 bg-slate-50 p-2 align-top text-xs font-black uppercase tracking-widest text-slate-500 sm:p-5">
                Plan features
              </th>
              {comparisonPlans.map((plan) => {
                const current = plan.id === access?.plan_id;
                const price = Number(plan.monthly_price || 0);
                const unavailableTrial = plan.plan_type === 'trial' && !plan.can_claim;

                return (
                  <th
                    key={plan.id}
                    className={`sticky top-0 z-20 w-[25.33%] border-b border-l border-slate-200 p-2 align-top sm:p-5 ${current ? 'bg-indigo-50/70' : 'bg-white'}`}
                  >
                    <div className="flex flex-wrap items-start justify-between gap-1 sm:gap-3">
                      <div>
                        <h3 className="break-words text-xl font-black text-slate-900">{plan.name}</h3>
                        <p className="mt-1 break-words text-sm text-slate-500">{plan.description}</p>
                      </div>
                      {current && <span className="rounded-full bg-emerald-50 px-3 py-1 text-[10px] font-black uppercase tracking-widest text-emerald-700">Current</span>}
                    </div>
                    <p className="mt-4 break-words text-lg font-black text-indigo-700">
                      {plan.plan_type === 'trial'
                        ? `${plan.trial_duration_days} day trial`
                        : plan.plan_type === 'free'
                          ? 'Free'
                          : `${String(plan.currency || 'cad').toUpperCase()} ${price.toFixed(2)} / month`}
                    </p>
                    {plan.plan_type === 'trial' && (
                      <p className="mt-1 break-words text-xs text-slate-500">
                        Claim again every {plan.claim_interval_months} months.
                        {unavailableTrial && plan.next_claim_at
                          ? ` Available ${new Date(plan.next_claim_at).toLocaleDateString()}.`
                          : ''}
                      </p>
                    )}
                    <button
                      type="button"
                      disabled={current || unavailableTrial || !!busy || plan.plan_type === 'free'}
                      onClick={() => choosePlan(plan)}
                      className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-indigo-600 px-2 py-3 text-xs font-black uppercase tracking-wider text-white disabled:bg-slate-200 disabled:text-slate-500 sm:px-4"
                    >
                      {busy === plan.id
                        ? <SafeIcon icon={FiLoader} className="animate-spin" />
                        : <SafeIcon icon={plan.plan_type === 'paid' ? FiCreditCard : FiCheck} />}
                      {current ? 'Current plan' : plan.plan_type === 'trial' ? 'Claim trial' : plan.plan_type === 'free' ? 'Included by default' : 'Choose plan'}
                    </button>
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {featureRows.length > 0 ? featureRows.map((feature) => (
              <tr key={feature}>
                <th
                  scope="row"
                  className="w-[24%] break-words border-b border-slate-100 bg-white p-2 text-sm font-bold text-slate-700 sm:p-5"
                >
                  {feature}
                </th>
                {comparisonPlans.map((plan) => {
                  const detail = plan.features?.[feature];
                  const included = Boolean(
                    detail && detail !== false && String(detail).toLowerCase() !== 'false'
                  );

                  return (
                    <td
                      key={plan.id}
                      className={`break-words border-b border-l border-slate-100 p-2 text-sm sm:p-5 ${plan.id === access?.plan_id ? 'bg-indigo-50/30' : ''}`}
                    >
                      {included ? (
                        <div className="flex items-start gap-2">
                          <span
                            aria-label="Included"
                            className="mt-1 h-2.5 w-2.5 shrink-0 rounded-full bg-emerald-500"
                          />
                          {detail !== true && (
                            <span className="break-words font-semibold text-slate-700">{String(detail)}</span>
                          )}
                        </div>
                      ) : (
                        <span aria-label="Not included" className="font-medium text-slate-300">—</span>
                      )}
                    </td>
                  );
                })}
              </tr>
            )) : (
              <tr>
                <td
                  colSpan={comparisonPlans.length + 1}
                  className="p-8 text-center text-sm font-medium text-slate-500"
                >
                  No plan features are available to compare yet.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {disabledPlans.length > 0 && (
        <aside className="rounded-2xl border border-amber-200 bg-amber-50 p-5">
          <h3 className="font-black text-amber-900">Currently unavailable</h3>
          <p className="mt-1 text-sm text-amber-800">
            These plans are disabled and cannot be selected.
          </p>
          <ul className="mt-3 flex flex-wrap gap-2">
            {disabledPlans.map((plan) => (
              <li key={plan.id} className="rounded-full border border-amber-200 bg-white px-3 py-1 text-xs font-bold text-amber-900">
                {plan.name} · Unavailable
              </li>
            ))}
          </ul>
        </aside>
      )}
    </section>
  );
};

export default SellerMembershipPlanTab;
